import i18n from 'i18next';
import {
    FONT_SIZE,
    FontSize,
    FormThemeKey,
    IPresentationTheme,
    QuestionElement,
    QuestionThemeKey,
    ShapeThemeKey,
    StaticContentElement,
    StaticThemeKey,
    getFontSizeTranslationKey,
} from '../incase-domain/presentationTheme';
import {ELEMENT} from '../incase-domain/elements/base/ElementTypes';
import {Option} from '../ui/components/common/Select';
import {IPresentation} from '../incase-domain/presentation';

export function setTextColor(
    size = 2,
    presentationTheme?: IPresentationTheme,
    selectedPresentation?: IPresentation
): {font: {color: string; size: number}} {
    const fontBlack = {font: {color: '#000000', size}};
    if (
        presentationTheme?.elementStyles?.textElement &&
        presentationTheme.elementStyles.textElement.color &&
        selectedPresentation?.presentationThemeId
    ) {
        fontBlack.font.color = presentationTheme.elementStyles.textElement.color;
    }
    if (
        presentationTheme?.elementStyles?.textElement &&
        presentationTheme.elementStyles.textElement.fontSize?.value &&
        selectedPresentation?.presentationThemeId
    ) {
        fontBlack.font.size = presentationTheme.elementStyles.textElement.fontSize.value;
    }
    return fontBlack;
}

export function getPresentationThemeFontSizeOptions(fontSizes: FontSize[] | undefined): Option[] {
    const options: Option[] = [];

    const sortedFontSizes =
        fontSizes &&
        fontSizes.slice().sort((a, b) => {
            const orderA = Object.values(FONT_SIZE).indexOf(a.size as FONT_SIZE);
            const orderB = Object.values(FONT_SIZE).indexOf(b.size as FONT_SIZE);
            return orderA - orderB;
        });

    if (sortedFontSizes) {
        sortedFontSizes.forEach((fontSize) => {
            const key = getFontSizeTranslationKey(fontSize.size);
            const localized = key ? i18n.t(key) : '';
            options.push({
                label: `${localized}: ${fontSize.value}` || '',
                value: fontSize.value?.toString() || '',
            });
        });
    } else {
        options.push({label: i18n.t('presentationThemesPage.noFontSizeOptions'), value: ''});
    }
    return options;
}

export function getUpdatedPresentationThemeElementStyles(presentationTheme: IPresentationTheme, size: FONT_SIZE, newValue: number): void {
    const syncFontSize = (holder: {fontSize?: FontSize} | undefined): void => {
        if (holder?.fontSize?.size === size) {
            // eslint-disable-next-line no-param-reassign
            holder.fontSize.value = newValue;
        }
    };

    if (presentationTheme.elementStyles) {
        const {elementStyles} = presentationTheme;
        syncFontSize(elementStyles.textElement);

        allQuestionThemeKeys.forEach((key) => {
            const questionTheme = elementStyles[key];
            if (!questionTheme) return;
            syncFontSize({fontSize: questionTheme.questionFontSize});
            syncFontSize({fontSize: questionTheme.answerFontSize});
            syncFontSize({fontSize: questionTheme.commentFontSize});
        });

        syncFontSize(elementStyles.htmlElement);
        syncFontSize(elementStyles.buttonElement);
        syncFontSize(elementStyles.textInputElement);
        syncFontSize(elementStyles.formElement);
    }
}

export const questionElementTypeToThemeKey: Partial<Record<ELEMENT, QuestionThemeKey>> = {
    [ELEMENT.QUESTION]: 'questionElement',
    [ELEMENT.YES_NO_QUESTION_V2]: 'yesNoQuestionElement',
    [ELEMENT.MULTI_SELECT]: 'multiSelectElement',
    [ELEMENT.PHOTO_ANSWER_QUESTION]: 'photoAnswerQuestionElement',
    [ELEMENT.SEQUENCE_QUESTION]: 'sequenceQuestionElement',
    [ELEMENT.MATCH_PAIR_QUESTION]: 'matchPairQuestionElement',
    [ELEMENT.LIKERT_SCALE]: 'likertScaleQuestionElement',
    [ELEMENT.IMAGE_MARKUP]: 'imageMarkupQuestionElement',
};

export const allQuestionThemeKeys: QuestionThemeKey[] = [
    'questionElement',
    'yesNoQuestionElement',
    'multiSelectElement',
    'photoAnswerQuestionElement',
    'sequenceQuestionElement',
    'matchPairQuestionElement',
    'likertScaleQuestionElement',
    'imageMarkupQuestionElement',
];

export function getEffectiveQuestionTheme(
    elementStyles: IPresentationTheme['elementStyles'],
    elementType: ELEMENT
): QuestionElement | undefined {
    const themeKey = questionElementTypeToThemeKey[elementType];
    if (!themeKey) return undefined;
    return elementStyles?.[themeKey];
}

export const staticElementTypeToThemeKey: Partial<Record<ELEMENT, StaticThemeKey>> = {
    [ELEMENT.STATIC_TEXT]: 'textElement',
    [ELEMENT.HTML]: 'htmlElement',
    [ELEMENT.IMAGE]: 'imageElement',
};

export function getEffectiveStaticTheme(
    elementStyles: IPresentationTheme['elementStyles'],
    themeKey: StaticThemeKey
): StaticContentElement | undefined {
    if (themeKey === 'imageElement') return elementStyles?.imageElement;
    return elementStyles?.[themeKey];
}

export function migrateLegacyThemeStyles(presentationTheme: IPresentationTheme): IPresentationTheme {
    const {elementStyles} = presentationTheme;
    if (!elementStyles) return presentationTheme;

    const deepCopy = <T>(value: T): T => (value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T));
    const nextStyles = {...elementStyles};

    if (elementStyles.questionElement) {
        allQuestionThemeKeys.forEach((key) => {
            if (key !== 'questionElement' && !nextStyles[key]) {
                nextStyles[key] = deepCopy(elementStyles.questionElement);
            }
        });
    }

    if (elementStyles.textElement && !nextStyles.htmlElement) {
        nextStyles.htmlElement = deepCopy(elementStyles.textElement);
    }

    const legacyStyles = nextStyles as {listElement?: unknown; tableElement?: unknown};
    delete legacyStyles.listElement;
    delete legacyStyles.tableElement;

    return {...presentationTheme, elementStyles: nextStyles};
}

export const formElementTypeToThemeKey: Partial<Record<ELEMENT, FormThemeKey>> = {
    [ELEMENT.BUTTON]: 'buttonElement',
    [ELEMENT.TEXT_INPUT]: 'textInputElement',
    [ELEMENT.FORM]: 'formElement',
};

export const shapeElementTypeToThemeKey: Partial<Record<ELEMENT, ShapeThemeKey>> = {
    [ELEMENT.RECTANGLE]: 'shapeElement',
    [ELEMENT.ELLIPSE]: 'shapeElement',
    [ELEMENT.TRIANGLE]: 'shapeElement',
    [ELEMENT.STAR]: 'shapeElement',
    [ELEMENT.CIRCLE]: 'shapeElement',
    [ELEMENT.LINE_V2]: 'lineElement',
    [ELEMENT.ARROW_V2]: 'lineElement',
};
